import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  Archive,
  ArrowRightLeft,
  CheckCircle2,
  ChevronDown,
  Clock,
  FilePlus2,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react'
import { templatesApi } from '../services/api'
import { formatAuditTimestamp, formatTimeAgo, getDateGroupLabel } from '../utils/dateUtils'

const PAGE_SIZE = 50

// ── Action metadata ─────────────────────────────────────────────────────────
// Monochrome palette: navy = the only accent (create/update/publish/config),
// gray = quiet lifecycle (archive), red = destructive (delete). Meaning is
// always reinforced by an icon + text label, never colour alone.
const ACTION_META = {
  created:        { label: 'Created',        icon: FilePlus2,        tone: 'navy' },
  updated:        { label: 'Updated',        icon: Pencil,           tone: 'navy' },
  config_changed: { label: 'Configuration',  icon: SlidersHorizontal, tone: 'navy' },
  published:      { label: 'Published',      icon: CheckCircle2,     tone: 'navy' },
  archived:       { label: 'Archived',       icon: Archive,          tone: 'gray' },
  deleted:        { label: 'Deleted',        icon: Trash2,           tone: 'red' },
  field_added:    { label: 'Field added',    icon: Plus,             tone: 'navy' },
  field_updated:  { label: 'Field edited',   icon: Pencil,           tone: 'navy' },
  field_removed:  { label: 'Field removed',  icon: Trash2,           tone: 'red' },
  field_restored: { label: 'Field restored', icon: RotateCcw,        tone: 'navy' },
  opco_changed:   { label: 'OpCo changed',   icon: ArrowRightLeft,   tone: 'navy' },
  opco_sharing_changed: { label: 'OpCo sharing changed', icon: ArrowRightLeft, tone: 'navy' },
}

const getActionMeta = (action) =>
  ACTION_META[action] || {
    label: (action || 'Activity').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    icon: Activity,
    tone: 'gray',
  }

const TONE_STYLES = {
  navy: {
    dot: 'bg-navy-50 dark:bg-navy-900/30 text-navy-600 dark:text-navy-300 ring-navy-100 dark:ring-navy-900/50',
    badge: 'bg-navy-50 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300 ring-navy-100 dark:ring-navy-900/50',
  },
  gray: {
    dot: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ring-gray-200 dark:ring-gray-700',
    badge: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-200 dark:ring-gray-700',
  },
  red: {
    dot: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-red-100 dark:ring-red-900/40',
    badge: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 ring-red-100 dark:ring-red-900/40',
  },
}

// Pretty-print a stored value for the change diff view.
const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'On' : 'Off'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

const initialsOf = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?'

// ── Details (changes) view ──────────────────────────────────────────────────
function ActivityDetails({ details }) {
  if (!details || typeof details !== 'object') return null
  const changes = Array.isArray(details.changes) ? details.changes : []
  const extras = Object.entries(details).filter(
    ([k, v]) => k !== 'changes' && v !== null && v !== undefined && v !== '' && v !== 0,
  )
  if (changes.length === 0 && extras.length === 0) return null

  return (
    <div className="mt-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/40 ring-1 ring-gray-100 dark:ring-gray-800 px-3 py-2.5 space-y-1.5">
      {changes.map((c, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="font-medium text-gray-600 dark:text-gray-300 min-w-[120px]">{c.field}</span>
          <span className="text-gray-400 dark:text-gray-500 line-through truncate max-w-[160px]">{formatValue(c.old)}</span>
          <span className="text-gray-300 dark:text-gray-600">→</span>
          <span className="text-gray-700 dark:text-gray-200 font-medium truncate max-w-[160px]">{formatValue(c.new)}</span>
        </div>
      ))}
      {extras.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <span className="font-medium text-gray-600 dark:text-gray-300 min-w-[120px] capitalize">{k.replace(/_/g, ' ')}</span>
          <span className="text-gray-700 dark:text-gray-200">{formatValue(v)}</span>
        </div>
      ))}
    </div>
  )
}

// ── A single timeline row ───────────────────────────────────────────────────
function TimelineItem({ entry, isLast }) {
  const [expanded, setExpanded] = useState(false)
  const meta = getActionMeta(entry.action)
  const Icon = meta.icon
  const tone = TONE_STYLES[meta.tone] || TONE_STYLES.gray
  const hasDetails = !!(entry.details && (
    (Array.isArray(entry.details.changes) && entry.details.changes.length > 0) ||
    Object.keys(entry.details).some((k) => k !== 'changes')
  ))

  return (
    <li className="relative flex gap-3.5 pb-5 last:pb-0">
      {/* Connector line */}
      {!isLast && (
        <span
          className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-800"
          aria-hidden="true"
        />
      )}
      {/* Action dot */}
      <span className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ring-1 ${tone.dot}`}>
        <Icon className="h-4 w-4" />
      </span>

      {/* Content */}
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${tone.badge}`}>
            {meta.label}
          </span>
          <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">
            {entry.description || meta.label}
          </p>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-[8px] font-semibold text-gray-600 dark:text-gray-300">
              {initialsOf(entry.actor_name)}
            </span>
            <span className="font-medium text-gray-600 dark:text-gray-300">{entry.actor_name || 'Unknown'}</span>
          </span>
          <span className="text-gray-300 dark:text-gray-600">•</span>
          <span className="inline-flex items-center gap-1" title={formatAuditTimestamp(entry.created_at)}>
            <Clock className="h-3 w-3" />
            {formatTimeAgo(entry.created_at)}
          </span>
          {entry.reason && (
            <>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <span className="italic">{entry.reason}</span>
            </>
          )}
          {hasDetails && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="inline-flex items-center gap-0.5 text-navy-600 dark:text-navy-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400 rounded"
            >
              {expanded ? 'Hide details' : 'View details'}
              <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {expanded && hasDetails && <ActivityDetails details={entry.details} />}
      </div>
    </li>
  )
}

export default function TemplateActivity({ templateId, templateName }) {
  const [entries, setEntries] = useState([])
  const [total, setTotal] = useState(0)
  const [facetActions, setFacetActions] = useState([])
  const [facetActors, setFacetActors] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters] = useState({ search: '', action: '', user_id: '', from: '', to: '' })

  const debounceRef = useRef(null)

  // Debounce the free-text search into the applied filters.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }))
    }, 350)
    return () => debounceRef.current && clearTimeout(debounceRef.current)
  }, [searchInput])

  const buildParams = useCallback((offset) => {
    const params = { limit: PAGE_SIZE, offset }
    if (filters.search) params.search = filters.search
    if (filters.action) params.action = filters.action
    if (filters.user_id) params.user_id = filters.user_id
    if (filters.from) params.from = filters.from
    if (filters.to) params.to = `${filters.to}T23:59:59`
    return params
  }, [filters])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await templatesApi.getActivity(templateId, buildParams(0))
      const data = res.data || {}
      setEntries(data.entries || [])
      setTotal(data.total || 0)
      setFacetActions(data.actions || [])
      setFacetActors(data.actors || [])
    } catch (e) {
      console.error('Failed to load template activity', e)
      setError(e?.response?.data?.error || 'Failed to load activity log.')
      setEntries([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [templateId, buildParams])

  useEffect(() => { load() }, [load])

  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      const res = await templatesApi.getActivity(templateId, buildParams(entries.length))
      const data = res.data || {}
      setEntries((prev) => [...prev, ...(data.entries || [])])
      setTotal(data.total || 0)
    } catch (e) {
      console.error('Failed to load more activity', e)
    } finally {
      setLoadingMore(false)
    }
  }, [templateId, buildParams, entries.length])

  const hasActiveFilters = !!(filters.search || filters.action || filters.user_id || filters.from || filters.to)

  const clearFilters = () => {
    setSearchInput('')
    setFilters({ search: '', action: '', user_id: '', from: '', to: '' })
  }

  // Group entries into dated buckets for the timeline.
  const groups = useMemo(() => {
    const map = new Map()
    for (const e of entries) {
      const label = getDateGroupLabel(e.created_at)
      if (!map.has(label)) map.set(label, [])
      map.get(label).push(e)
    }
    return Array.from(map.entries())
  }, [entries])

  const selectClass =
    'text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2.5 py-2 focus:ring-2 focus:ring-navy-400/30 focus:border-navy-400 disabled:opacity-40'

  return (
    <div className="space-y-4">
      {/* Intro banner */}
      <div className="flex items-start gap-3 rounded-xl shadow-sm ring-1 ring-gray-100 dark:ring-gray-800 bg-gray-50/50 dark:bg-gray-800/30 px-4 py-3">
        <Activity className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          A complete, tamper-evident record of every action taken on
          {templateName ? <strong className="text-gray-700 dark:text-gray-300"> “{templateName}”</strong> : ' this template'}
          {' '}— creation, updates, publishing, archiving, deletion, and configuration changes — with who did what and when.
        </div>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search activity, user, or field…"
              aria-label="Search activity log"
              className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-navy-400/30 focus:border-navy-400"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Action filter */}
          <select
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
            aria-label="Filter by action"
            className={selectClass}
          >
            <option value="">All actions</option>
            {facetActions.map((a) => (
              <option key={a} value={a}>{getActionMeta(a).label}</option>
            ))}
          </select>

          {/* User filter */}
          <select
            value={filters.user_id}
            onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
            aria-label="Filter by user"
            className={selectClass}
          >
            <option value="">All users</option>
            {facetActors.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          {/* Date range */}
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              aria-label="From date"
              className={selectClass}
            />
            <span className="text-xs text-gray-400">–</span>
            <input
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              aria-label="To date"
              className={selectClass}
            />
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-2"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        {loading ? (
          <div className="space-y-4" aria-busy="true">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-3.5">
                <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-3.5 w-1/2 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
                  <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="p-2.5 rounded-full bg-red-50 dark:bg-red-900/20 mb-3">
              <X className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{error}</p>
            <button onClick={load} className="btn btn-secondary text-xs mt-3 !px-3 !py-1.5">Retry</button>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-800 mb-3">
              <Activity className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {hasActiveFilters ? 'No activity matches your filters' : 'No activity recorded yet'}
            </p>
            <p className="text-xs text-gray-400 mt-1 max-w-xs">
              {hasActiveFilters
                ? 'Try adjusting or clearing the filters to see more history.'
                : 'Actions performed on this template will appear here as an audit trail.'}
            </p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn btn-secondary text-xs mt-3 !px-3 !py-1.5 gap-1.5">
                <RotateCcw className="h-3.5 w-3.5" /> Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(([label, items]) => (
              <div key={label}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</h3>
                  <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                </div>
                <ul className="list-none">
                  {items.map((entry, idx) => (
                    <TimelineItem
                      key={entry.id}
                      entry={entry}
                      isLast={idx === items.length - 1}
                    />
                  ))}
                </ul>
              </div>
            ))}

            {/* Load more */}
            {entries.length < total && (
              <div className="flex justify-center pt-1">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn btn-secondary text-xs !px-4 !py-2 gap-1.5 disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  Load more ({total - entries.length} older)
                </button>
              </div>
            )}
            <p className="text-center text-[11px] text-gray-400 pt-1">
              Showing {entries.length} of {total} {total === 1 ? 'event' : 'events'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
